import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BoardState, Color } from '@draughts/shared';
import PuzzlePage from '../puzzle/PuzzlePage';
import type { AIDifficulty } from './DashboardPlayAIModal';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import type { LocalPuzzle } from '../../data/demo-puzzle';
import './DashboardPuzzleModal.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface Props {
  isOpen: boolean;
  puzzleId: number;
  practice?: boolean;
  /** Client-side demo puzzle (no auth); takes precedence over puzzleId. */
  localPuzzle?: LocalPuzzle;
  /** Conversion CTA: opens the register (or, for a guest, upgrade) modal. */
  onRegister?: () => void;
  /** Offer Share on a solved daily puzzle — registered users only (see PuzzlePage). */
  canShare?: boolean;
  /** Solved-puzzle "keep playing vs AI": host seeds the game and navigates. */
  onContinueVsAI?: (board: BoardState, humanColor: Color, difficulty: AIDifficulty) => void;
  onClose: () => void;
}

/**
 * Mobile-only full-screen sheet that hosts the (unchanged) PuzzlePage, so the
 * daily-puzzle "Risolvi" button opens an overlay like the other dashboard play
 * modals ("Gioca subito" / "Sfida AI") instead of switching to a separate
 * screen. Desktop keeps the full PuzzlePage screen — App.tsx branches on
 * useIsMobile and only opens this sheet on phones.
 *
 * It reuses the `play-online-modal` full-screen-mobile CSS (100dvh, hides the
 * bottom nav, edge-to-edge); `.puzzle-modal` only adds the floating close
 * button and lets the puzzle own its own scroll. The PuzzlePage itself renders
 * untouched.
 */
export default function DashboardPuzzleModal({ isOpen, puzzleId, practice, localPuzzle, onRegister, canShare, onContinueVsAI, onClose }: Props) {
  const { t } = useTranslation();
  const isDemo = !!localPuzzle;

  // Device/browser back gesture closes the sheet (matches the play modals).
  useOverlayBackButton(isOpen, onClose);

  // Esc to close.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  /*
   * Il fuoco dentro il dialogo, Tab che non ne esce, e alla chiusura il fuoco
   * torna a chi l'aveva. Ruolo, nome ed Escape c'erano già; il fuoco no — e senza
   * di lui la tabulazione continua nella pagina SOTTO, che è ancora lì dietro il
   * velo, su bottoni che nessuno vede.
   *
   * Chiamato PRIMA del `return null` qui sotto — un hook non può stare dietro a
   * una condizione — con `isOpen` come interruttore. E senza `onClose`: l'Escape
   * lo gestisce già il componente, passarlo anche all'hook chiuderebbe due volte.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(isOpen);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal dashboard-modal play-online-modal puzzle-modal"
        ref={riferimentoDialogo}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={isDemo ? t('puzzle.demo.badge', 'Demo puzzle') : t('puzzle.daily', 'Daily Puzzle')}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="play-online-close puzzle-modal-close"
          aria-label={t('common.close', 'Close')}
          onClick={onClose}
        >
          <X size={20} aria-hidden="true" />
        </button>
        <PuzzlePage
          onBack={onClose}
          initialPuzzleId={puzzleId || undefined}
          isDaily={!isDemo}
          practice={!!practice}
          localPuzzle={localPuzzle}
          onRegister={onRegister}
          canShare={canShare}
          onContinueVsAI={onContinueVsAI}
          embedded
        />
      </div>
    </div>
  );
}
